import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';

const EditMonth = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    number: '',
    title: '',
    sortDescription: ''
  });
  const [faqs, setFaqs] = useState([{ question: '', answer: '' }]);
  const [recentFaqs, setRecentFaqs] = useState([{ question: '', answer: '' }]);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchMonthData();
  }, [id]);

  const fetchMonthData = async () => {
    try {
      const response = await apiService.get(`editMonth/${id}`);
      const monthData = response.data.data;
      
      setFormData({
        name: monthData.name || '',
        number: monthData.number || '',
        title: monthData.title || '',
        sortDescription: monthData.sortDescription || ''
      });
      
      setFaqs(monthData.faq && monthData.faq.length > 0 ? monthData.faq : [{ question: '', answer: '' }]);
      setRecentFaqs(monthData.recentFaq && monthData.recentFaq.length > 0 ? monthData.recentFaq : [{ question: '', answer: '' }]);
      
      if (monthData.image) {
        setImagePreview(`${process.env.REACT_APP_MEDIA_URL}${monthData.image}`);
      }
      
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch month data');
      console.error(error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        toast.error('File size must be 1MB or less');
        e.target.value = '';
        return;
      }
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const data = new FormData();
    data.append('id', id);
    data.append('name', formData.name);
    data.append('number', formData.number);
    data.append('title', formData.title);
    data.append('sortDescription', formData.sortDescription);
    
    faqs.filter(f => f.question?.trim() || f.answer?.trim()).forEach((faq, i) => {
      data.append(`faq[${i}][question]`, faq.question || '');
      data.append(`faq[${i}][answer]`, faq.answer || '');
    });

    recentFaqs.filter(f => f.question?.trim() || f.answer?.trim()).forEach((faq, i) => {
      data.append(`recentFaq[${i}][question]`, faq.question || '');
      data.append(`recentFaq[${i}][answer]`, faq.answer || '');
    });
    
    if (image) data.append('image', image);

    try {
      await apiService.postFormData('updateMonth', data);
      toast.success('Month updated successfully');
      navigate('/view-months');
    } catch (error) {
      toast.error('Failed to update month');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009ef7]"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-0">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Edit Month</h1>
        <nav className="text-sm">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/location" className="text-gray-500 hover:text-gray-700">Locations</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/view-months" className="text-gray-500 hover:text-gray-700">View Months</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Edit Month</span>
        </nav>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Image</label>
              <div className="relative w-64 h-44 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <input type="file" accept="image/*" onChange={handleImageChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
              </div>
            </div>

            <div className="col-span-8 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Month Name <span className="text-red-500">*</span></label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter month name" required className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Month Number <span className="text-red-500">*</span></label>
                  <input type="number" name="number" value={formData.number} onChange={handleInputChange} placeholder="Enter month number (1-12)" min="1" max="12" required className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input type="text" name="title" value={formData.title} onChange={handleInputChange} placeholder="Enter month title" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Short Description</label>
                <textarea name="sortDescription" value={formData.sortDescription} onChange={handleInputChange} placeholder="Enter short description" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <label className="block text-lg font-semibold text-gray-900 mb-4">FAQ Section</label>
          {faqs.map((faq, index) => (
            <div key={index} className="border border-gray-300 rounded-lg p-4 mb-3">
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                <input type="text" value={faq.question} onChange={(e) => { const updated = [...faqs]; updated[index].question = e.target.value; setFaqs(updated); }} placeholder="Enter FAQ question" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                <textarea value={faq.answer} onChange={(e) => { const updated = [...faqs]; updated[index].answer = e.target.value; setFaqs(updated); }} placeholder="Enter FAQ answer" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <button type="button" onClick={() => setFaqs(faqs.filter((_, i) => i !== index))} className="px-2 sm:px-4 py-2 text-sm border border-red-500 text-red-500 rounded-lg hover:bg-red-50">Remove FAQ</button>
            </div>
          ))}
          <div className="text-right">
            <button type="button" onClick={() => setFaqs([...faqs, { question: '', answer: '' }])} className="px-2 sm:px-4 py-2 text-sm border border-[#009ef7] text-[#009ef7] rounded-lg hover:bg-[#009ef7]/10">Add FAQ</button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <label className="block text-lg font-semibold text-gray-900 mb-4">Recent FAQ Section</label>
          {recentFaqs.map((faq, index) => (
            <div key={index} className="border border-gray-300 rounded-lg p-4 mb-3">
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                <input type="text" value={faq.question} onChange={(e) => { const updated = [...recentFaqs]; updated[index].question = e.target.value; setRecentFaqs(updated); }} placeholder="Enter Recent FAQ question" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                <textarea value={faq.answer} onChange={(e) => { const updated = [...recentFaqs]; updated[index].answer = e.target.value; setRecentFaqs(updated); }} placeholder="Enter Recent FAQ answer" rows="3" className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <button type="button" onClick={() => setRecentFaqs(recentFaqs.filter((_, i) => i !== index))} className="px-2 sm:px-4 py-2 text-sm border border-red-500 text-red-500 rounded-lg hover:bg-red-50">Remove Recent FAQ</button>
            </div>
          ))}
          <div className="text-right">
            <button type="button" onClick={() => setRecentFaqs([...recentFaqs, { question: '', answer: '' }])} className="px-2 sm:px-4 py-2 text-sm border border-[#009ef7] text-[#009ef7] rounded-lg hover:bg-[#009ef7]/10">Add Recent FAQ</button>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Link to="/view-months" className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</Link>
          <button type="submit" disabled={submitting} className="px-6 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 disabled:opacity-50">
            {submitting ? 'Updating...' : 'Update'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditMonth;
